import React, { useMemo, useState } from "react";
import { ActionIcon, Box, Menu, Text } from "@mantine/core";
import {
  DndContext,
  DragEndEvent,
  Modifier,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { SalonService } from "../../../../types/salon";
import type { WaitlistEntry } from "../../../../types/waitlist";
import { DESIGN_ICONS } from "../../../../theme/designIcons";
import {
  arrivedClock,
  durationText,
  entryClientName,
  gripLabel,
  minutesWaited,
  moveIdBy,
  waitText,
} from "./waitlistModel";
import styles from "./Waitlist.module.css";

const {
  GripVertical,
  Clock,
  EllipsisVertical,
  Pencil,
  UserMinus,
  MessageSquareQuote,
  CircleCheck,
  ArrowUp,
  ArrowDown,
  User,
} = DESIGN_ICONS;

/** Card actions slot state (the assign picker highlights its card). */
export interface WaitlistCardSlot {
  onOpenChange: (open: boolean) => void;
}

export interface WaitlistListProps {
  entries: WaitlistEntry[];
  timezone: string;
  services: SalonService[];
  /** Epoch ms (ticking): the "(18 mins ago)" text. */
  now: number;
  /** "sheet": mobile bottom sheet (touch drag, Move up / down menu items). */
  variant?: "drawer" | "sheet";
  onReorder: (ids: string[]) => void;
  onRemove: (entry: WaitlistEntry) => void;
  onEdit?: (entry: WaitlistEntry) => void;
  /** Under the card's comments (Assign staff). */
  renderActions?: (
    entry: WaitlistEntry,
    slot: WaitlistCardSlot,
  ) => React.ReactNode;
}

// Drags start anywhere on a card except its controls (the grip excepted), so
// the menu / assign buttons keep their clicks (prototype `startDrag`).
const CONTROL_SELECTOR =
  "button,input,textarea,select,a,[role=menu],[role=menuitem],[role=listbox],[role=option]";
const startsOnControl = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== "function") return false;
  if (el.closest("[data-grip]")) return false;
  return !!el.closest(CONTROL_SELECTOR);
};

class CardPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: "onPointerDown" as const,
      handler: ({ nativeEvent }: React.PointerEvent) =>
        nativeEvent.isPrimary &&
        nativeEvent.button === 0 &&
        !startsOnControl(nativeEvent.target),
    },
  ];
}

class CardMouseSensor extends MouseSensor {
  static activators = [
    {
      eventName: "onMouseDown" as const,
      handler: ({ nativeEvent }: React.MouseEvent) =>
        nativeEvent.button === 0 && !startsOnControl(nativeEvent.target),
    },
  ];
}

class CardTouchSensor extends TouchSensor {
  static activators = [
    {
      eventName: "onTouchStart" as const,
      handler: ({ nativeEvent }: React.TouchEvent) =>
        nativeEvent.touches.length <= 1 && !startsOnControl(nativeEvent.target),
    },
  ];
}

const lockToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
});

/** Touch: a press-and-hold starts the drag, so a swipe still scrolls. */
const TOUCH_DRAG = { delay: 220, tolerance: 8 };

/**
 * The waitlist cards in line order: drag reorder (dnd-kit; mouse anywhere on
 * the card, touch press-and-hold on mobile, arrow keys on the focused grip),
 * ⋯ menu (Edit, Move up / down on mobile, Remove from waitlist), arrival
 * time with the elapsed wait, the requested provider (walk-in portal),
 * customer comments, the actions slot, and the
 * empty state.
 */
export default function WaitlistList({
  entries,
  timezone,
  services,
  now,
  variant = "drawer",
  onReorder,
  onRemove,
  onEdit,
  renderActions,
}: WaitlistListProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const ids = useMemo(() => entries.map((e) => e.uuid), [entries]);
  const colorOf = useMemo(() => {
    const map = new Map<string, string>();
    services.forEach((s) => {
      if (s.default_calendar_event_color)
        map.set(s.id, s.default_calendar_event_color);
    });
    return map;
  }, [services]);

  const pointer = useSensor(CardPointerSensor, {
    activationConstraint: { distance: 4 },
  });
  const mouse = useSensor(CardMouseSensor, {
    activationConstraint: { distance: 4 },
  });
  const touch = useSensor(CardTouchSensor, {
    activationConstraint: TOUCH_DRAG,
  });
  const desktopSensors = useSensors(pointer);
  const sheetSensors = useSensors(mouse, touch);

  const moveBy = (id: string, delta: number) => {
    const next = moveIdBy(ids, id, delta);
    if (next) onReorder(next);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(ids, from, to));
  };

  if (entries.length === 0) {
    return (
      <Box className={styles.empty} data-testid="waitlist-empty">
        <span className={styles.emptyIcon}>
          <CircleCheck size={22} />
        </span>
        <Text span className={styles.emptyTitle}>
          No one is waiting
        </Text>
        <Text span className={styles.emptyText}>
          Add a walk-in when someone arrives without an appointment.
        </Text>
      </Box>
    );
  }

  return (
    <DndContext
      sensors={variant === "sheet" ? sheetSensors : desktopSensors}
      collisionDetection={closestCenter}
      modifiers={[lockToVerticalAxis]}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <Box className={styles.list} role="list" aria-label="Waitlist">
          {entries.map((entry, index) => (
            <WaitlistCard
              key={entry.uuid}
              entry={entry}
              index={index}
              total={entries.length}
              timezone={timezone}
              now={now}
              color={colorOf.get(entry.salon_service_uuid) || "#8a8a8a"}
              variant={variant}
              active={activeId === entry.uuid}
              onMoveBy={(delta) => moveBy(entry.uuid, delta)}
              onRemove={() => onRemove(entry)}
              onEdit={onEdit ? () => onEdit(entry) : undefined}
              actions={renderActions?.(entry, {
                onOpenChange: (open) =>
                  setActiveId((cur) =>
                    open ? entry.uuid : cur === entry.uuid ? null : cur,
                  ),
              })}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
}

interface WaitlistCardProps {
  entry: WaitlistEntry;
  index: number;
  total: number;
  timezone: string;
  now: number;
  color: string;
  variant: "drawer" | "sheet";
  active: boolean;
  onMoveBy: (delta: number) => void;
  onRemove: () => void;
  onEdit?: () => void;
  actions?: React.ReactNode;
}

function WaitlistCard({
  entry,
  index,
  total,
  timezone,
  now,
  color,
  variant,
  active,
  onMoveBy,
  onRemove,
  onEdit,
  actions,
}: WaitlistCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.uuid });
  const name = entryClientName(entry);
  const comments = (entry.comments || "").trim();
  const requested = (entry.preferred_staff_name || "").trim();

  return (
    <Box
      ref={setNodeRef}
      role="listitem"
      className={styles.card}
      data-active={active || undefined}
      data-dragging={isDragging || undefined}
      data-testid={`waitlist-card-${entry.uuid}`}
      style={{
        transform: CSS.Transform.toString(
          transform && isDragging
            ? { ...transform, scaleX: 1.015, scaleY: 1.015 }
            : transform,
        ),
        transition,
      }}
      {...listeners}
    >
      <Box className={styles.cardRow}>
        <button
          type="button"
          ref={setActivatorNodeRef}
          data-grip="1"
          className={styles.grip}
          {...attributes}
          aria-label={gripLabel(name, index, total)}
          title="Drag to reorder"
          onKeyDown={(e) => {
            if (e.key === "ArrowUp") {
              e.preventDefault();
              onMoveBy(-1);
            } else if (e.key === "ArrowDown") {
              e.preventDefault();
              onMoveBy(1);
            }
          }}
        >
          <GripVertical size={16} />
        </button>
        <Box className={styles.main}>
          <Text span className={styles.client}>
            {name}
          </Text>
          <Text span className={styles.service}>
            <span className={styles.dot} style={{ background: color }} />
            <span className={styles.serviceText}>
              {entry.service_name} · {durationText(entry.service_duration_mins)}
            </span>
          </Text>
          <Text span className={styles.arrived}>
            <Clock size={12} className={styles.arrivedIcon} />
            <span>
              Arrived {arrivedClock(entry.arrived_at, timezone)}{" "}
              <span className={styles.waited}>
                ({waitText(minutesWaited(entry.arrived_at, now))})
              </span>
            </span>
          </Text>
          {requested && (
            <Text span className={styles.requested}>
              <User size={12} className={styles.arrivedIcon} />
              <span>Requested: {requested}</span>
            </Text>
          )}
        </Box>
        <Menu position="bottom-end" width={200} withinPortal>
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              size={30}
              radius="md"
              className={styles.menuBtn}
              aria-label="Walk-in actions"
            >
              <EllipsisVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {onEdit && (
              <Menu.Item leftSection={<Pencil size={15} />} onClick={onEdit}>
                Edit
              </Menu.Item>
            )}
            {variant === "sheet" && (
              <>
                <Menu.Item
                  leftSection={<ArrowUp size={15} />}
                  disabled={index === 0}
                  onClick={() => onMoveBy(-1)}
                >
                  Move up
                </Menu.Item>
                <Menu.Item
                  leftSection={<ArrowDown size={15} />}
                  disabled={index === total - 1}
                  onClick={() => onMoveBy(1)}
                >
                  Move down
                </Menu.Item>
              </>
            )}
            <Menu.Item
              className={styles.danger}
              leftSection={<UserMinus size={15} />}
              onClick={onRemove}
            >
              Remove from waitlist
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Box>

      {comments && (
        <Box className={styles.comments}>
          <Box className={styles.commentsLabel}>
            <MessageSquareQuote size={12} />
            Customer Comments
          </Box>
          <Box className={styles.commentsText}>{comments}</Box>
        </Box>
      )}

      {actions && <Box className={styles.actions}>{actions}</Box>}
    </Box>
  );
}
